import React, { useRef } from 'react';
import { BodySmall400, TitleMedium700, TitleXSmall400 } from '@nebula/global-styles';
import { BaseLozengeBadge, badgeSizeEnum } from '@nebula/badge';
import { RadioButtonState, RadioButtonType } from '@nebula/radio-button';

import {
  CardContainer,
  CardMain,
  CardRadioContainer,
  CardRadio,
  CardContent,
  PricingText,
  DiscountBadgeContainer,
  MonthlyEquivalentBar,
  HeaderRow,
  StruckPrice,
  PlanLabel,
} from './index.styles';

type PricingTermCardProps = {
  id: string;
  termText: string;
  priceText: string;
  frequencyText: string;
  billingText: string;
  discountBadgeText?: string;
  discountedPriceText?: string;
  monthlyEquivalentText?: string;
  isSelected?: boolean;
  hideRadio?: boolean;
  onSelect: (id: string) => void;
};

const PricingTermCard = ({
  id,
  termText,
  priceText,
  frequencyText,
  billingText,
  discountBadgeText,
  discountedPriceText,
  monthlyEquivalentText,
  isSelected = false,
  hideRadio = false,
  onSelect,
}: PricingTermCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Clicking the radio's own label already delegates natively to the input (firing a real
  // `change` event Snowplow's form tracking relies on). Clicking anywhere else on the card
  // has to forward the click to the input directly so that native event still fires, rather
  // than only updating React state via `onSelect`.
  const triggerNativeRadioSelection = (event: React.SyntheticEvent) => {
    const inputEl = cardRef.current?.querySelector<HTMLInputElement>('input[type="radio"]');
    if (!inputEl) return;
    const radioLabel = inputEl.closest('label');
    if (radioLabel?.contains(event.target as Node)) return;
    inputEl.click();
  };

  const hasFooter = !!monthlyEquivalentText;

  return (
    <CardContainer
      ref={cardRef}
      role="radio"
      aria-checked={isSelected}
      aria-label={termText}
      isSelected={isSelected}
      $hasFooter={hasFooter}
      onClick={triggerNativeRadioSelection}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          triggerNativeRadioSelection(event);
        }
      }}
      tabIndex={0}
      data-testid={id}
    >
      <CardMain $hasFooter={hasFooter} $hideRadio={hideRadio}>
        {!hideRadio && (
          <CardRadioContainer>
            <CardRadio
              id={id}
              value={String(id)}
              label=""
              width="1.25rem"
              index={0}
              isChecked={isSelected}
              isDisabled={false}
              radioType={RadioButtonType.Standard}
              radioState={RadioButtonState.Standard}
              onRadioButtonSelected={() => onSelect(id)}
              aria-hidden="false"
            />
          </CardRadioContainer>
        )}

        <CardContent $hasFooter={hasFooter}>
          <HeaderRow>
            <PlanLabel>{termText}</PlanLabel>
            {discountBadgeText && (
              <DiscountBadgeContainer>
                <BaseLozengeBadge
                  badgeSize={badgeSizeEnum.small}
                  text={discountBadgeText}
                  backgroundColour="#d9efe5"
                  textColour="#115740"
                  iconColour="#115740"
                />
              </DiscountBadgeContainer>
            )}
          </HeaderRow>

          <PricingText>
            {discountedPriceText && <StruckPrice>{priceText}</StruckPrice>}
            <TitleMedium700 as="span">{discountedPriceText ?? priceText}</TitleMedium700>
            <TitleXSmall400 as="span">{frequencyText}</TitleXSmall400>
          </PricingText>

          <BodySmall400 as="span">{billingText}</BodySmall400>
        </CardContent>
      </CardMain>

      {monthlyEquivalentText && (
        <MonthlyEquivalentBar $hasFooter={hasFooter} $hideRadio={hideRadio}>
          {monthlyEquivalentText}
        </MonthlyEquivalentBar>
      )}
    </CardContainer>
  );
};

export default PricingTermCard;
